import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "drawer", "backdrop", "opener", "content" ]

  connect() {
    this._mediaQuery = window.matchMedia("(min-width: 1024px)")
    this._onBreakpointChange = this.#syncForViewport.bind(this)
    this._mediaQuery.addEventListener("change", this._onBreakpointChange)
    this.#syncForViewport()
  }

  disconnect() {
    this._mediaQuery?.removeEventListener("change", this._onBreakpointChange)
    document.body.classList.remove("overflow-hidden")
    if (this.hasContentTarget) {
      this.contentTarget.inert = false
      this.contentTarget.removeAttribute("aria-hidden")
    }
  }

  open(event) {
    if (this._mediaQuery.matches) return

    this._returnFocus = event?.currentTarget || document.activeElement
    this.drawerTarget.inert = false
    this.drawerTarget.classList.remove("-translate-x-full")
    this.backdropTarget.classList.remove("hidden")
    this.contentTarget.inert = true
    this.contentTarget.setAttribute("aria-hidden", "true")
    this.openerTarget.setAttribute("aria-expanded", "true")
    document.body.classList.add("overflow-hidden")
    requestAnimationFrame(() => this.#focusableElements()[0]?.focus())
  }

  close() {
    if (this._mediaQuery.matches) return

    this.drawerTarget.classList.add("-translate-x-full")
    this.drawerTarget.inert = true
    this.backdropTarget.classList.add("hidden")
    this.contentTarget.inert = false
    this.contentTarget.removeAttribute("aria-hidden")
    this.openerTarget.setAttribute("aria-expanded", "false")
    document.body.classList.remove("overflow-hidden")
    this._returnFocus?.focus()
  }

  closeOnEscape(event) {
    if (event.key === "Escape" && !this.drawerTarget.classList.contains("-translate-x-full")) this.close()
  }

  trapFocus(event) {
    if (event.key !== "Tab" || this._mediaQuery.matches) return

    const elements = this.#focusableElements()
    if (elements.length === 0) return

    const first = elements[0]
    const last = elements[elements.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  #syncForViewport() {
    this.backdropTarget.classList.add("hidden")
    this.openerTarget.setAttribute("aria-expanded", "false")
    document.body.classList.remove("overflow-hidden")
    this.contentTarget.inert = false
    this.contentTarget.removeAttribute("aria-hidden")

    if (this._mediaQuery.matches) {
      this.drawerTarget.classList.remove("-translate-x-full")
      this.drawerTarget.inert = false
    } else {
      this.drawerTarget.classList.add("-translate-x-full")
      this.drawerTarget.inert = true
    }
  }

  #focusableElements() {
    return [...this.drawerTarget.querySelectorAll("a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex='-1'])")]
      .filter(element => !element.hidden && element.offsetParent !== null)
  }
}
